import { getSupabase } from './client';
import type { Json, JsonObject, Tables } from './database.types';
import { DataError, toDataError } from './errors';
import { asJsonObject, asNumber, asString, isJsonObject, toJson } from './json';
import type { WorkspaceScope } from './workspace';

/** A module document as the UI sees it: stored JSON plus server-owned metadata. */
export type RecordDoc = JsonObject & {
  id: string;
  rev: number;
  createdAt: string;
  updatedAt: string;
};

export interface RecordDelta {
  readonly created: RecordDoc[];
  readonly updated: RecordDoc[];
  readonly deleted: string[];
  /** Highest server updated_at seen (or the input cursor when nothing changed). */
  readonly cursor: string;
}

export type UpsertOutcome =
  | { readonly status: 'ok'; readonly record: RecordDoc }
  | { readonly status: 'conflict'; readonly record: RecordDoc };

const PAGE_SIZE = 1000;
/** Re-read a few seconds before the cursor so rows committed slightly out of order are not missed. */
const DELTA_OVERLAP_MS = 5000;
/** Keep each import RPC payload well under the PostgREST request size limit. */
const IMPORT_BATCH_BYTES = 1_500_000;

function isoOf(timestamp: string): string {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toISOString();
}

export function rowToDoc(row: Pick<Tables<'records'>, 'id' | 'data' | 'rev' | 'created_at' | 'updated_at'>): RecordDoc {
  return {
    ...asJsonObject(row.data),
    id: row.id,
    rev: row.rev,
    createdAt: isoOf(row.created_at),
    updatedAt: isoOf(row.updated_at),
  };
}

function jsonToDoc(value: Json | undefined): RecordDoc {
  const obj = asJsonObject(value);
  const id = asString(obj.id);
  if (!id) throw new DataError('invalid', 'Server returned a record without an id.');
  return {
    ...obj,
    id,
    rev: asNumber(obj.rev, 1),
    createdAt: asString(obj.createdAt),
    updatedAt: asString(obj.updatedAt),
  };
}

export interface RecordsRepository {
  readonly scope: WorkspaceScope;
  list(module: string): Promise<RecordDoc[]>;
  get(module: string, id: string): Promise<{ record: RecordDoc | null; deleted: boolean }>;
  delta(module: string, cursor: string): Promise<RecordDelta>;
  upsert(module: string, record: JsonObject): Promise<UpsertOutcome>;
  remove(module: string, id: string): Promise<void>;
  importRows(module: string, rows: unknown[]): Promise<number>;
  purgeDemo(): Promise<number>;
  resetAll(): Promise<number>;
}

export function createRecordsRepository(scope: WorkspaceScope): RecordsRepository {
  const supabase = getSupabase();

  const baseQuery = (module: string) =>
    supabase
      .from('records')
      .select('id, data, rev, created_at, updated_at, deleted')
      .eq('workspace_id', scope.workspaceId)
      .eq('app_key', scope.appKey)
      .eq('module', module);

  return {
    scope,

    async list(module) {
      const docs: RecordDoc[] = [];
      for (let from = 0; ; from += PAGE_SIZE) {
        const { data, error } = await baseQuery(module)
          .eq('deleted', false)
          .order('updated_at', { ascending: false })
          .order('id')
          .range(from, from + PAGE_SIZE - 1);
        if (error) throw toDataError(error);
        const rows = data ?? [];
        for (const row of rows) docs.push(rowToDoc(row));
        if (rows.length < PAGE_SIZE) break;
      }
      return docs;
    },

    async get(module, id) {
      const { data, error } = await baseQuery(module).eq('id', id).maybeSingle();
      if (error) throw toDataError(error);
      if (!data) return { record: null, deleted: false };
      return { record: data.deleted ? null : rowToDoc(data), deleted: data.deleted };
    },

    async delta(module, cursor) {
      const created: RecordDoc[] = [];
      const updated: RecordDoc[] = [];
      const deleted: string[] = [];
      let highest = cursor;

      const cursorMs = cursor ? Date.parse(cursor) : Number.NaN;
      const since = Number.isNaN(cursorMs) ? null : new Date(cursorMs - DELTA_OVERLAP_MS).toISOString();

      for (let from = 0; ; from += PAGE_SIZE) {
        let query = baseQuery(module);
        if (since) query = query.gt('updated_at', since);
        const { data, error } = await query
          .order('updated_at', { ascending: true })
          .order('id')
          .range(from, from + PAGE_SIZE - 1);
        if (error) throw toDataError(error);
        const rows = data ?? [];
        for (const row of rows) {
          const updatedAt = isoOf(row.updated_at);
          if (!highest || updatedAt > highest) highest = updatedAt;
          if (row.deleted) {
            deleted.push(row.id);
            continue;
          }
          const doc = rowToDoc(row);
          if (!since || doc.createdAt > since) created.push(doc);
          else updated.push(doc);
        }
        if (rows.length < PAGE_SIZE) break;
      }
      return { created, updated, deleted, cursor: highest };
    },

    async upsert(module, record) {
      const { data, error } = await supabase.rpc('record_upsert', {
        p_workspace: scope.workspaceId,
        p_app: scope.appKey,
        p_module: module,
        p_record: record,
      });
      if (error) throw toDataError(error);
      const result = asJsonObject(data);
      const doc = jsonToDoc(result.record);
      return result.status === 'conflict' ? { status: 'conflict', record: doc } : { status: 'ok', record: doc };
    },

    async remove(module, id) {
      const { error } = await supabase.rpc('record_delete', {
        p_workspace: scope.workspaceId,
        p_app: scope.appKey,
        p_module: module,
        p_id: id,
      });
      if (error) throw toDataError(error);
    },

    async importRows(module, rows) {
      const objects = rows.map((row) => toJson(row)).filter(isJsonObject);
      let total = 0;
      let batch: Json[] = [];
      let batchBytes = 0;

      const flush = async () => {
        if (batch.length === 0) return;
        const { data, error } = await supabase.rpc('records_import', {
          p_workspace: scope.workspaceId,
          p_app: scope.appKey,
          p_module: module,
          p_rows: batch,
        });
        if (error) throw toDataError(error);
        total += data ?? 0;
        batch = [];
        batchBytes = 0;
      };

      for (const obj of objects) {
        const size = JSON.stringify(obj).length;
        if (batch.length > 0 && batchBytes + size > IMPORT_BATCH_BYTES) await flush();
        batch.push(obj);
        batchBytes += size;
      }
      await flush();
      return total;
    },

    async purgeDemo() {
      const { data, error } = await supabase.rpc('records_purge_demo', {
        p_workspace: scope.workspaceId,
        p_app: scope.appKey,
      });
      if (error) throw toDataError(error);
      return data ?? 0;
    },

    async resetAll() {
      const { data, error } = await supabase.rpc('records_reset', {
        p_workspace: scope.workspaceId,
        p_app: scope.appKey,
      });
      if (error) throw toDataError(error);
      return data ?? 0;
    },
  };
}
